<script setup>
import {ref, toRef} from "vue";
import {FormTextarea, FormInput} from "@/base-components/Form";
import Lucide from "@/base-components/Lucide";
import avatar from "@/assets/images/avatar.png";
import Button from "@/base-components/Button";
import ConfirmationDialog from "@/base-components/Dialog/ConfirmationDialog.vue";
import {uri} from "@/constants/config.js";

const props = defineProps({
  data: {
    type: Object,
    default: null
  },
  isLoading: {
    type: Boolean,
    default: false
  },
})
const showCloseTicketFlag = ref(false);
const loading = toRef(props, 'isLoading');
const defaultMessage = {
  text: null,
  file: null
}
const message = ref({
  text: null,
  file: null
})
const emit = defineEmits(['send', 'close']);

function sendMessage() {
  if (message.value.text) {
    emit('send',message.value);
    Object.assign(message.value, defaultMessage)
  }
}

function cancelCloseTicket() {
  showCloseTicketFlag.value = false
}

function confirmCloseTicket() {
  showCloseTicketFlag.value = false
  emit('close')
}

function attachFile(event) {
  if (event.target.files.length === 0) return;
  message.value.file = event.target.files[0]
}
</script>

<template>
  <div class="grid grid-cols-12 gap-2 mt-5 intro-y" v-if="data">

    <div v-if="showCloseTicketFlag">
      <ConfirmationDialog
          :close="cancelCloseTicket"
          :show="showCloseTicketFlag"
          :accept="confirmCloseTicket"
          :deny="cancelCloseTicket"
      >
        {{ $t('message.are-you-sure-close-ticket') }} {{ data.title ?? '' }}
      </ConfirmationDialog>
    </div>

    <!-- BEGIN: Chat Content -->
    <div class="col-span-12 intro-y ">
      <div class="h-[760px] box">
        <!-- BEGIN: Chat Active -->
        <div class="flex flex-col h-full">
          <div
              class="flex flex-row  px-4 py-1 border-b sm:flex-row border-slate-200/60 dark:border-darkmode-400"
          >
            <div class="flex items-center w-full">
              <div
                  class="relative flex-none w-10 h-10 sm:w-12 sm:h-12 image-fit"
              >
                <Lucide icon="MessageSquare" class="w-8 h-8 mt-2"/>
              </div>
              <div class="ms-3 me-auto">
                <div class="text-xl font-bold">
                  {{ data?.Department?.name }} - {{ data?.title }}
                </div>
              </div>
            </div>
            <div class="min-w-32 pt-1.5 items-end   justify-between">
              <Button
                  v-if="data.isOpen"
                  @click="showCloseTicketFlag=true"
                  size="lg"
                  variant="outline-dark"
                  class="float-end text-sm"
              >
                {{ $t("button.close-ticket") }}
              </Button>
            </div>
          </div>
          <div class="flex-1 px-5 pt-5 overflow-y-scroll scrollbar-hidden">
            <div
                v-for="(chat, index) in data?.TicketMessages"
                :key="index"
                class="flex w-full relative"
            >
              <!-- Owner's message -->
              <div
                  class="flex items-end ml-auto mb-4 max-w-[90%] sm:max-w-[49%]"
                  v-if="data.User.id===chat.User.id"
              >

                <div class="relative flex-none hidden w-10 h-10 me-5 sm:block image-fit">
                  <div>
                    <img
                        :alt="avatar"
                        class="rounded-full"
                        :src="avatar"
                    />
                  </div>
                </div>

                <div class="px-4 py-3 bg-slate-100 dark:bg-darkmode-400 text-slate-800 rounded-r-md rounded-t-md">

                  {{ chat.description }}
                  <div class="mt-1 text-[9px] text-primary text-opacity-80">
                    {{ chat.createdAt }}
                  </div>
                  <a v-if="chat.TicketAttachments && chat.TicketAttachments.length > 0" target="_blank"
                     :href="uri.api.ticket.attachmentId(chat.TicketAttachments[0].guid)">
                    <Lucide icon="LucidePaperclip"/>
                    {{ chat.TicketAttachments[0].name }}
                  </a>
                </div>
              </div>

              <!-- Other's message -->
              <div
                  v-else
                  class="flex items-end mr-auto mb-4 max-w-[90%] sm:max-w-[49%]"
              >

                <div class="px-4 py-3 text-white bg-primary rounded-l-md rounded-t-md">
                  {{ chat.description }}
                  <div class="mt-1 text-[9px] text-white text-opacity-80">
                    {{ chat.createdAt }}
                  </div>
                  <a v-if="chat.TicketAttachments && chat.TicketAttachments.length > 0" target="_blank"
                     :href="uri.api.ticket.attachmentId(chat.TicketAttachments[0].guid)">
                    <Lucide icon="LucidePaperclip"/>
                    {{ chat.TicketAttachments[0].name }}
                  </a>
                </div>

                <div class="relative flex-none hidden w-10 h-10 ms-5 sm:block image-fit">
                  <img
                      :alt="avatar"
                      class="rounded-full"
                      :src="avatar"
                  />
                </div>
              </div>
            </div>

          </div>
          <div v-if="loading">
            <FreeStyleShimmer height="60px" width="100%" border-radius="0px 0px 5px 5px"/>
          </div>
          <div
              v-else
              class="flex items-center pt-4 pb-10 border-t sm:py-4 border-slate-200/60 dark:border-darkmode-400 px-2"
          >
            <FormTextarea
                v-model="message.text"
                class="px-5   border-transparent shadow-none resize-none h-[60px] dark:bg-darkmode-600 focus:border-transparent focus:ring-0 me-2"
                :rows="3"
                :disabled="!data.isOpen"
                :placeholder="$t('placeholder.type-message')"
            />
            <div>
            </div>
            <Button
                :variant="data.isOpen?'primary':'secondary'"
                @click="sendMessage"
                :disabled="!data.isOpen"
                class="flex items-center cursor-pointer justify-center flex-none w-8 h-8 me-5 rounded-full sm:w-10 sm:h-10"
            >
              <Lucide icon="Send" class="w-4 h-4 -rotate-90 "/>
            </Button>

            <div class="relative">
              <Button
                  :variant="message.file?'warning':'primary'"
                  :disabled="!data.isOpen"
                  class="flex items-center cursor-pointer justify-center flex-none w-8 h-8 me-5 rounded-full sm:w-10 sm:h-10"
                  @click="() =>message.file=null"
              >
                <Lucide v-if="!message.file" icon="Paperclip" class="w-4 h-4 -rotate-90 "/>
                <Lucide v-else icon="FileMinus" class="w-6 h-6 "/>
              </Button>
              <FormInput
                  v-if="!message.file"
                  :disabled="!data.isOpen"
                  type="file"
                  class="absolute top-0 left-0 w-full h-full opacity-0"
                  @change="attachFile"
              />
            </div>
          </div>
        </div>
        <!-- END: Chat Active -->
        <!-- END: Chat Default -->
      </div>
    </div>
    <!-- END: Chat Content -->
  </div>
</template>
